import { useState } from 'react';
import fetchData from 'libs/fetch';
import { POST_LORA_FLUX_PLG_IMG2IMG_MASK } from 'api'; // Adjust based on your endpoint constant

interface GenerateImageResponse {
  image_urls: string [];
  record_ids: string [];
}

interface Lora {
  path: string;
  scale: number;
}


interface UseFalForLoraImgToImgMask {
  loading: boolean;
  error: string | null;
  generateImage: (
    imageFile: File,
    maskB64: string,
    prompt: string,
    constantLora: Lora,
    strength?: number,
    numImages?: number,
    extraLoras?: Lora[]
  ) => Promise<GenerateImageResponse | null>;
}

export default function useFalForLoraImgToImgMask(): UseFalForLoraImgToImgMask {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateImage = async (
    imageFile: File,
    maskB64: string,
    prompt: string,
    constantLora: Lora,
    strength: number = 0.85,
    numImages: number = 1,
    extraLoras: Lora[] = [] 
  ): Promise<GenerateImageResponse | null> => {
    setLoading(true);
    setError(null);

      // Validate numImages (only allow 1, 2, 3, or 4)
    const allowedNumImages = [1, 2, 3, 4];
      if (!allowedNumImages.includes(numImages)) {
        setError("Invalid number of images. Allowed values are: 1, 2, 3, or 4.");
        setLoading(false);
        return null;
      }
  
    // Set the LoRA path dynamically based on modelUsername
    const loras = [constantLora, ...extraLoras];

    // Prepare FormData
    const formData = new FormData();
    formData.append('image_file', imageFile);
    formData.append('mask_b64', maskB64); // Add the painted mask in base64 format
    formData.append('prompt', prompt);
    formData.append('image_size', 'portrait_16_9');
    formData.append('num_inference_steps', '20');
    formData.append('guidance_scale', '3.5');
    formData.append('num_images', numImages.toString()); 
    formData.append('output_format', 'jpeg');
    formData.append('enable_safety_checker', 'false');
    formData.append('loras', JSON.stringify(loras));
    formData.append('strength', strength.toString());

    try {
      const response = await fetchData(POST_LORA_FLUX_PLG_IMG2IMG_MASK, {
        method: 'POST',
        body: formData,
      });
       // Validate and return response as GenerateImageResponse
       if (response.image_urls && Array.isArray(response.image_urls)) {
        return {
          image_urls: response.image_urls, // Return array of image URLs
          record_ids: response.record_ids, // Return array of record IDs
        } as GenerateImageResponse;
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (err: any) {
      setError(err.detail || 'Error generating image');
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, generateImage };
}
